/**
 * cart-shared.js
 * ─────────────────────────────────────────────────────────────────
 * LOAD ORDER: Must be the FIRST deferred script. All other cart-*.js
 * files depend on the globals defined here.
 *
 * Provides:
 *   window.plumGetSharedCart(cb)    — read cart (cached / deduped)
 *   window.plumBustSharedCart()     — invalidate cache
 *   window.plumSetSharedCart(cart)  — write cache externally
 *   window.plumRefreshCart(res)     — update cache + dispatch cart:refresh
 *   window.isWidgetEnabled(key)     — feature-flag check
 *   window.getWidgetSettings()      — parse #widgetStatus-mini-cart
 *
 * HOW THE SINGLE-FETCH GUARANTEE WORKS
 * ──────────────────────────────────────
 * All cart-*.js files load with `defer` and execute in DOM order.
 * cart-shared.js runs first and starts the cart.js fetch IMMEDIATELY
 * at parse time — NOT inside a DOMContentLoaded listener.
 *
 * By the time every other module's init code runs (also on
 * DOMContentLoaded), _inflight is already a live Promise. Any call
 * to plumGetSharedCart() while the fetch is in-flight just attaches
 * a .then() to that same Promise — zero extra network requests.
 * ─────────────────────────────────────────────────────────────────
 */

// ─── Widget Settings ─────────────────────────────────────────────────────────

window.getWidgetSettings = function getWidgetSettings() {
  if (window.WIDGET_SETTINGS && typeof window.WIDGET_SETTINGS === 'object') {
    return window.WIDGET_SETTINGS;
  }

  var el = document.getElementById('widgetStatus-mini-cart');
  if (!el) return {};

  var raw = (el.textContent || '').trim();
  if (!raw) return {};

  try { return JSON.parse(raw); } catch (_) {}

  try {
    var match = raw.match(/window\.WIDGET_SETTINGS\s*=\s*({[\s\S]*?})\s*;?\s*$/);
    if (match && match[1]) return JSON.parse(match[1]);
  } catch (_) {}

  console.error('[cart-shared] Invalid widget settings in #widgetStatus-mini-cart');
  return {};
};

window.isWidgetEnabled = function isWidgetEnabled(key) {
  var settings = window.getWidgetSettings();
  return Boolean(settings && settings[key] && settings[key].enabled);
};

// ─── Shared Cart Cache ────────────────────────────────────────────────────────

(function () {
  var _cache     = null;
  var _inflight  = null;   // single in-flight Promise — the dedup key
  var _stale     = true;
  var SECTION_ID = 'mini-cart';

  function isCart(cart) {
    return !!(cart && Array.isArray(cart.items));
  }

  function setCart(cart) {
    if (!isCart(cart)) return;
    _cache    = cart;
    _stale    = false;
    _inflight = null;   // clear so next read returns from _cache fast-path
  }

  // ── Internal fetch — only ever called when no fetch is in flight ──────────
  function _doFetch() {
    _inflight = fetch(window.Shopify.routes.root + 'cart.js', {
      credentials: 'same-origin'
    })
      .then(function (r) {
        if (!r.ok) throw new Error('[cart-shared] cart.js HTTP ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        setCart(cart);
        return cart;
      })
      .catch(function (err) {
        _inflight = null;   // allow retry on hard failure
        console.error('[cart-shared] preload failed:', err);
        throw err;
      });

    return _inflight;
  }

  // ── Public API ────────────────────────────────────────────────────────────

  window.plumGetSharedCart = function (cb) {
    // 1. Cache hit — synchronous, free
    if (_cache && !_stale) { cb(_cache); return; }

    // 2. Fetch already in flight — piggyback, zero extra requests
    if (_inflight) { _inflight.then(cb).catch(function () {}); return; }

    // 3. Cache busted and nothing in flight — start a fresh fetch
    //    (only reached after an explicit plumBustSharedCart() call)
    _doFetch().then(cb).catch(function () {});
  };

  window.plumBustSharedCart = function () {
    _stale    = true;
    _inflight = null;   // next plumGetSharedCart will re-fetch
  };

  window.plumSetSharedCart = setCart;

  window.plumRefreshCart = function (res) {
    window._plumOwnRefresh = true;

    var detail = {};

    if (isCart(res)) {
      setCart(res);
      if (res.sections && res.sections[SECTION_ID]) {
        detail.cart = res;
      }
    } else {
      _stale    = true;
      _inflight = null;
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true, detail: detail })
    );

    setTimeout(function () { window._plumOwnRefresh = false; }, 0);
  };

  // Keep cache warm when the theme fires its own cart events
  document.addEventListener('cart:updated', function (event) {
    var cart = event && event.detail && event.detail.cart;
    if (isCart(cart)) setCart(cart);
  });

  // ── CRITICAL: preload starts HERE — synchronously, at parse time ─────────
  //
  // Because this file has `defer`, it runs after HTML is parsed but
  // BEFORE DOMContentLoaded fires. Starting the fetch right now means
  // _inflight is a live Promise before any other module's DOMContentLoaded
  // listener executes. All five feature modules will hit branch (2) above
  // and piggyback on this one request instead of making their own.
  _doFetch();

})();